// src/salaryCalculations.js
import { TAX_YEARS } from './constants/taxConstants';

const safeNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

const safeToFixed = (value, precision = 2) => {
  try {
    return safeNumber(value).toFixed(precision);
  } catch (error) {
    return '0.00';
  }
};

// Helper Functions for RSU vest events calculations
const calculateTotalRsuIncome = (rsuVestEvents) => {
  if (!rsuVestEvents || rsuVestEvents.length === 0) return 0;
  return rsuVestEvents.reduce((total, vest) => total + vest.amount, 0);
};

// Calculate total tax withheld from RSU vests at time of vesting
const calculateRsuTaxWithheld = (rsuVestEvents) => {
  if (!rsuVestEvents || rsuVestEvents.length === 0) return 0;
  return rsuVestEvents.reduce((total, vest) => 
    total + (vest.amount * (vest.withholding_rate / 100)), 0);
};

// Calculate weighted average RSU tax rate
const calculateAverageRsuTaxRate = (rsuVestEvents) => {
  if (!rsuVestEvents || rsuVestEvents.length === 0) return 0;
  
  const totalAmount = rsuVestEvents.reduce((sum, vest) => sum + vest.amount, 0);
  if (totalAmount === 0) return 0;
  
  const weightedRateSum = rsuVestEvents.reduce((sum, vest) => 
    sum + (vest.amount * vest.withholding_rate), 0);
    
  return weightedRateSum / totalAmount;
};

// Calculate total capital gains from RSU vests that weren't sold immediately
const calculateRsuCapitalGains = (rsuVestEvents) => {
  if (!rsuVestEvents || rsuVestEvents.length === 0) return 0;
  return rsuVestEvents.reduce((total, vest) => 
    total + (vest.sold_immediately ? 0 : vest.capital_gains), 0);
};

// Helper Functions for calculations
const calculateRsuSubjectToIncomeTax = (rsuVestEvents, applyEmployerNic = true) => {
  if (!rsuVestEvents || rsuVestEvents.length === 0) return 0;
  
  return rsuVestEvents.reduce((total, vest) => {
    const employerNICRate = vest.employer_nic_rate || 6.9; // Default to 6.9% if not specified
    
    // Apply employer NIC only if the flag is true
    const employerNICAmount = applyEmployerNic ? vest.amount * (employerNICRate / 100) : 0;
    const rsuSubjectToTax = vest.amount - employerNICAmount;
    
    return total + rsuSubjectToTax;
  }, 0);
};

const calculateTaxableIncome = (baseSalary, bonus, pensionContribution, medicalBenefit, rsuSubjectToIncomeTax) => {
  return baseSalary + bonus - pensionContribution + medicalBenefit + rsuSubjectToIncomeTax;
};

const calculatePersonalAllowanceReduction = (taxableIncome, threshold, maxAllowance) => {
  return Math.max(maxAllowance - Math.max((taxableIncome - threshold) / 2, 0), 0);
};

const calculateUKTax = (taxableIncome, taxConstants) => {
  const {
    TAX_BASIC_RATE_LIMIT,
    TAX_BASIC_RATE,
    TAX_HIGHER_RATE_LIMIT,
    TAX_HIGHER_RATE,
    TAX_ADDITIONAL_RATE
  } = taxConstants;

  const basicRateTaxableIncome = Math.min(taxableIncome, TAX_BASIC_RATE_LIMIT);
  const higherRateTaxableIncome = taxableIncome > TAX_BASIC_RATE_LIMIT 
    ? Math.min((taxableIncome - TAX_BASIC_RATE_LIMIT), (TAX_HIGHER_RATE_LIMIT - TAX_BASIC_RATE_LIMIT)) 
    : 0;
  const additionalRateTaxableIncome = taxableIncome > TAX_HIGHER_RATE_LIMIT 
    ? (taxableIncome - TAX_HIGHER_RATE_LIMIT) 
    : 0;

  const taxBasicRate = basicRateTaxableIncome * TAX_BASIC_RATE;
  const taxHigherRate = higherRateTaxableIncome * TAX_HIGHER_RATE;
  const taxAdditionalRate = additionalRateTaxableIncome * TAX_ADDITIONAL_RATE;

  return {
    total: taxBasicRate + taxHigherRate + taxAdditionalRate,
    basicRate: taxBasicRate,
    higherRate: taxHigherRate,
    additionalRate: taxAdditionalRate
  };
};

const calculateScottishTax = (taxableIncome, personalAllowanceReduction, taxConstants) => {
  const {
    STARTER_RATE,
    BASIC_RATE,
    INTERMEDIATE_RATE,
    HIGHER_RATE,
    TOP_RATE,
    STARTER_RATE_LIMIT,
    BASIC_RATE_LIMIT,
    INTERMEDIATE_RATE_LIMIT,
    HIGHER_RATE_LIMIT,
    ADVANCED_RATE_LIMIT
  } = taxConstants;

  const starterRateTaxableIncome = Math.min(taxableIncome, STARTER_RATE_LIMIT);
  const basicRateTaxableIncome = taxableIncome > STARTER_RATE_LIMIT 
    ? Math.min((taxableIncome - STARTER_RATE_LIMIT), (BASIC_RATE_LIMIT - STARTER_RATE_LIMIT)) 
    : 0;
  const intermediateRateTaxableIncome = taxableIncome > BASIC_RATE_LIMIT 
    ? Math.min((taxableIncome - BASIC_RATE_LIMIT), (INTERMEDIATE_RATE_LIMIT - BASIC_RATE_LIMIT)) 
    : 0;
  const higherRateTaxableIncome = taxableIncome > INTERMEDIATE_RATE_LIMIT 
    ? Math.min((taxableIncome - INTERMEDIATE_RATE_LIMIT), (HIGHER_RATE_LIMIT - INTERMEDIATE_RATE_LIMIT)) 
    : 0;
  const advancedRateTaxableIncome = (taxableIncome > HIGHER_RATE_LIMIT 
    ? Math.min((taxableIncome - HIGHER_RATE_LIMIT), (ADVANCED_RATE_LIMIT - HIGHER_RATE_LIMIT)) 
    : 0) + (12570 - personalAllowanceReduction);
  const topRateTaxableIncome = taxableIncome > ADVANCED_RATE_LIMIT 
    ? (taxableIncome - ADVANCED_RATE_LIMIT) 
    : 0;

  const taxStarterRate = (starterRateTaxableIncome - 12570) * STARTER_RATE;
  const taxBasicRate = basicRateTaxableIncome * BASIC_RATE;
  const taxIntermediateRate = intermediateRateTaxableIncome * INTERMEDIATE_RATE;
  const taxHigherRate = higherRateTaxableIncome * HIGHER_RATE;
  const taxAdvancedRate = advancedRateTaxableIncome * HIGHER_RATE;
  const taxTopRate = topRateTaxableIncome * TOP_RATE;

  return {
    total: taxStarterRate + taxBasicRate + taxIntermediateRate + taxHigherRate + taxAdvancedRate + taxTopRate,
    starterRate: taxStarterRate,
    basicRate: taxBasicRate,
    intermediateRate: taxIntermediateRate,
    higherRate: taxHigherRate,
    advancedRate: taxAdvancedRate,
    topRate: taxTopRate
  };
};

const calculateNIC = (taxableIncome, rsus, taxConstants) => {
  const { NIC_PRIMARY_THRESHOLD, NIC_UPPER_EARNINGS_LIMIT, NIC_UPPER_RATE, NIC_LOWER_RATE } = taxConstants;

  const nicLowerEarnings = Math.min(
    Math.max((taxableIncome - NIC_PRIMARY_THRESHOLD), 0),
    (NIC_UPPER_EARNINGS_LIMIT - NIC_PRIMARY_THRESHOLD)
  );
  const nicUpperEarnings = Math.max((taxableIncome - NIC_UPPER_EARNINGS_LIMIT), 0);
  const nicLowerAmount = nicLowerEarnings * NIC_LOWER_RATE;
  const nicUpperAmount = nicUpperEarnings * NIC_UPPER_RATE;
  const nicRSUs = rsus * NIC_UPPER_RATE;
  
  return nicLowerAmount + nicUpperAmount + nicRSUs;
};

const calculateCGT = (capitalGains, taxableIncome, taxConstants) => {
  const { CGT_EXEMPT_AMOUNT, CGT_BASIC_RATE_LIMIT, CGT_LOWER_RATE, CGT_HIGHER_RATE } = taxConstants;
  
  const cgtRate = taxableIncome <= CGT_BASIC_RATE_LIMIT ? CGT_LOWER_RATE : CGT_HIGHER_RATE;
  const cgtTaxableGains = Math.max(capitalGains - CGT_EXEMPT_AMOUNT, 0);
  return cgtRate * cgtTaxableGains;
};

// Calculate PAYE for individual vest events
const calculateVestPAYE = (vestEvents, isTaxResidentInScotland, applyEmployerNic = true) => {
  if (!vestEvents || vestEvents.length === 0) return [];
  
  // Get the default tax rate based on residence
  const defaultTaxRate = isTaxResidentInScotland ? 42 : 40;
  
  return vestEvents.map(vest => {
    // Calculate the amount after employer NIC
    const employerNicRate = vest.employer_nic_rate || 6.9;

    // Apply employer NIC only if the flag is true
    const employerNicAmount = applyEmployerNic ? vest.amount * (employerNicRate/100) : 0;
    const subjectToTax = vest.amount - employerNicAmount;
    
    // Calculate what was withheld at vest time
    const withholdingRate = vest.withholding_rate || 50;
    const withholdingAmount = vest.amount * (withholdingRate/100);
    
    // Calculate what PAYE would show on the payslip for this month
    const payeAmount = subjectToTax * (defaultTaxRate/100);
    
    // Difference between PAYE and withholding (positive means additional tax due)
    const difference = payeAmount - withholdingAmount;
    
    return {
      ...vest,
      estimated_paye: {
        subject_to_tax: subjectToTax,
        withholding_amount: withholdingAmount,
        paye_amount: payeAmount,
        difference: difference,
        employer_nic_applied: applyEmployerNic
      }
    };
  });
};


/**
 * Calculate student loan repayments based on income and selected plans
 * 
 * @param {number} taxableIncome - The total taxable income
 * @param {array} studentLoanPlans - Array of selected student loan plan types
 * @param {object} taxConstants - Tax constants for the selected tax year
 * @returns {object} - Student loan repayment details with total and breakdowns
 */
const calculateStudentLoanRepayments = (taxableIncome, studentLoanPlans = [], taxConstants) => {
  if (!studentLoanPlans || studentLoanPlans.length === 0) {
    return {
      total: 0,
      breakdown: {},
      hasRepayments: false
    };
  }

  const { STUDENT_LOANS } = taxConstants;
  
  // Initialize the result object
  const result = {
    total: 0,
    breakdown: {},
    hasRepayments: false
  };

  // Keep track of income that's already been used for a repayment
  // (to handle the rule that income is only assessed once for Plan 1, 2, 4, and 5)
  let incomeAlreadyAssessed = 0;
  
  // Calculate repayments for each selected plan
  studentLoanPlans.forEach(planType => {
    if (!STUDENT_LOANS[planType]) return;
    
    const { THRESHOLD, RATE } = STUDENT_LOANS[planType];
    let repaymentAmount = 0;
    
    if (planType === 'POSTGRAD') {
      // Postgraduate loan is calculated independently
      repaymentAmount = Math.max(0, (taxableIncome - THRESHOLD) * RATE);
      if (repaymentAmount > 0) result.hasRepayments = true;
      result.breakdown[planType] = repaymentAmount;
    } else {
      // For Plans 1, 2, 4, and 5, income is only assessed once
      // Calculate repayment on the remaining unassessed income
      const relevantIncome = Math.max(0, taxableIncome - incomeAlreadyAssessed);
      
      repaymentAmount = Math.max(0, (relevantIncome - THRESHOLD) * RATE);
      
      if (repaymentAmount > 0) {
        result.hasRepayments = true;
        
        // Mark the assessed income for future plans
        incomeAlreadyAssessed = Math.max(incomeAlreadyAssessed, THRESHOLD);
      }
      
      result.breakdown[planType] = repaymentAmount;
    }
    
    result.total += repaymentAmount;
  });
  
  return result;
};

// ==================== Refactored calculation function ====================

/**
 * Calculates salary details for a specific pension contribution percentage
 * @param {Object} inputs - The input values for the calculation
 * @param {string} taxYear - The tax year to use for the calculation
 * @param {number} employeePensionContributionPct - The employee pension contribution percentage
 * @param {number} resultId - The ID to assign to the result object
 * @returns {Object} The calculated salary details
 */
const calculateSalaryDetailsForPensionPct = (inputs, taxYear, employeePensionContributionPct, resultId) => {
  const {
    baseSalary,
    bonus,
    medicalBenefit,
    voldeductions,
    rsus,
    capitalGains,
    rsuemployerNICrate,
    employerMaxPensionContributionPct,
    isTaxResidentInScotland,
    rsuTaxRate = 50,
    rsuVestEvents = [],
    studentLoanPlans = [],
    isEmployerNicLiable = true,
    // New: Additional Benefits & Salary Sacrifice (annualised)
    childcareVouchers = 0,
    cycleToWork = 0,
    electricCar = 0,
    companyCarBenefit = 0,
  } = inputs;

  // Get the appropriate tax constants for the selected year and region
  const taxConstants = TAX_YEARS[taxYear][isTaxResidentInScotland ? 'SCOTLAND' : 'UK'];
  taxConstants.STUDENT_LOANS = TAX_YEARS[taxYear].STUDENT_LOANS;

  // Use the isEmployerNicLiable value directly from inputs
  const applyEmployerNic = isEmployerNicLiable;

  // Get the default behavior based on tax year (for explanatory purposes)
  const is2025OrLater = taxYear === "2025/2026" || parseInt(taxYear.split('/')[0]) >= 2025;
  const defaultIsEmployerNicLiable = !is2025OrLater;

  const employerPensionContributionPct = Math.min(employerMaxPensionContributionPct, employeePensionContributionPct);

  // Convert inputs to numbers
  const numericBaseSalaryRaw = safeNumber(baseSalary);
  const numericBonus = safeNumber(bonus);
  const numericRSUs = rsuVestEvents && rsuVestEvents.length > 0 ? calculateTotalRsuIncome(rsuVestEvents) : safeNumber(rsus);    
  const numericCG = rsuVestEvents && rsuVestEvents.length > 0 ? calculateRsuCapitalGains(rsuVestEvents) : safeNumber(capitalGains);
  const numericVolDeductions = safeNumber(voldeductions);
  const numericMedicalBenefit = safeNumber(medicalBenefit);
  const numericChildcare = safeNumber(childcareVouchers);
  const numericCycleToWork = safeNumber(cycleToWork);
  const numericElectricCar = safeNumber(electricCar);
  const numericCompanyCar = safeNumber(companyCarBenefit);

  // Apply salary sacrifice reductions (cannot reduce below zero)
  const totalSalarySacrifice = Math.max(0, numericChildcare + numericCycleToWork + numericElectricCar);
  const numericBaseSalary = Math.max(0, numericBaseSalaryRaw - totalSalarySacrifice);

  // Calculate pension amounts
  const employeePensionAmount = numericBaseSalary * (employeePensionContributionPct / 100);
  const employerPensionAmount = numericBaseSalary * (employerPensionContributionPct / 100);
  const totalPensionAmount = employeePensionAmount + employerPensionAmount;

  // Updated: RSU calculations with employer NIC liability option
  let RSUssubjecttoincometax;
  if (rsuVestEvents && rsuVestEvents.length > 0) {
    // Calculate from vest events, respecting applyEmployerNic flag
    RSUssubjecttoincometax = calculateRsuSubjectToIncomeTax(rsuVestEvents, applyEmployerNic);
  } else {
    // Simple calculation for rsus input, respecting applyEmployerNic flag
    RSUssubjecttoincometax = applyEmployerNic 
      ? numericRSUs - (numericRSUs * (rsuemployerNICrate / 100))
      : numericRSUs;
  }

  // Calculate gross pay
  const gross_pay = numericBaseSalary + numericBonus + numericRSUs;
  const gross_pay_ex_rsu = numericBaseSalary + numericBonus;

  // Calculate taxable income
  const taxableIncomeLessPension = calculateTaxableIncome(
    numericBaseSalary, 
    numericBonus, 
    employeePensionAmount, 
    numericMedicalBenefit + numericCompanyCar, 
    RSUssubjecttoincometax
  );
  const taxableIncomeLessPensionexclRSUs = calculateTaxableIncome(
    numericBaseSalary,
    numericBonus,
    employeePensionAmount,
    numericMedicalBenefit + numericCompanyCar,
    0
  );

  const studentLoanRepayments = calculateStudentLoanRepayments(
    taxableIncomeLessPension, 
    studentLoanPlans, 
    taxConstants
  );

  const studentLoanRepaymentsExclRSUs = calculateStudentLoanRepayments(
    taxableIncomeLessPensionexclRSUs, 
    studentLoanPlans, 
    taxConstants
  );

  // Calculate personal allowance reductions
  const personalallowanceReduction = calculatePersonalAllowanceReduction(
    taxableIncomeLessPension,
    taxConstants.ANNUAL_PERSONAL_ALLOWANCE_THRESHOLD,
    taxConstants.ANNUAL_PERSONAL_ALLOWANCE
  );
  const personalallowanceReductionexclRSUs = calculatePersonalAllowanceReduction(
    taxableIncomeLessPensionexclRSUs,
    taxConstants.ANNUAL_PERSONAL_ALLOWANCE_THRESHOLD,
    taxConstants.ANNUAL_PERSONAL_ALLOWANCE
  );

  // Calculate taxable income less personal allowance
  const taxableIncomeLessPandPA = Math.max((taxableIncomeLessPension - personalallowanceReduction), 0);
  const taxableIncomeLessPandPAexclRSUs = Math.max((taxableIncomeLessPensionexclRSUs - personalallowanceReductionexclRSUs), 0);

  // Calculate full tax (for reference)
  const taxResult = isTaxResidentInScotland 
    ? calculateScottishTax(taxableIncomeLessPandPA, personalallowanceReduction, taxConstants)
    : calculateUKTax(taxableIncomeLessPandPA, taxConstants);

  // Calculate tax excluding RSUs
  const taxResultExclRSUs = isTaxResidentInScotland
    ? calculateScottishTax(taxableIncomeLessPandPAexclRSUs, personalallowanceReductionexclRSUs, taxConstants)
    : calculateUKTax(taxableIncomeLessPandPAexclRSUs, taxConstants);

  // Calculate the tax already withheld on RSUs at vest time
  const rsuTaxWithheld = rsuVestEvents && rsuVestEvents.length > 0 ? calculateRsuTaxWithheld(rsuVestEvents) : numericRSUs * (rsuTaxRate / 100);

  // Get the effective RSU tax rate to display
  const effectiveRsuTaxRate = rsuVestEvents && rsuVestEvents.length > 0 ? rsuVestEvents.reduce((total, vest) => total + (vest.amount * vest.withholding_rate), 0) / rsuVestEvents.reduce((total, vest) => total + vest.amount, 0) : rsuTaxRate;

  // Get the average RSU tax rate
  const averageRsuTaxRate = rsuVestEvents && rsuVestEvents.length > 0 ? calculateAverageRsuTaxRate(rsuVestEvents) : rsuTaxRate;

  // Calculate CGT correctly and explicitly
  const cgt_rate = taxableIncomeLessPension <= taxConstants.CGT_BASIC_RATE_LIMIT 
  ? taxConstants.CGT_LOWER_RATE 
  : taxConstants.CGT_HIGHER_RATE;

  const cgt_taxable_gains = numericCG > taxConstants.CGT_EXEMPT_AMOUNT 
  ? (numericCG - taxConstants.CGT_EXEMPT_AMOUNT) 
  : 0;

  const capital_gains_tax = cgt_rate * cgt_taxable_gains;

  // HMRC approach for calculating actual RSU tax
  const calculateActualRSUTax = () => {
    // First calculate tax on total income (already calculated above in taxResult)
    const totalTax = taxResult.total;

    // Then calculate what tax would be without the RSUs
    const taxableIncomeWithoutRSUs = taxableIncomeLessPension - RSUssubjecttoincometax;
    const personalAllowanceWithoutRSUs = calculatePersonalAllowanceReduction(
      taxableIncomeWithoutRSUs,
      taxConstants.ANNUAL_PERSONAL_ALLOWANCE_THRESHOLD,
      taxConstants.ANNUAL_PERSONAL_ALLOWANCE
    );
    
    const taxableIncomeWithoutRSUsLessPA = Math.max(
      (taxableIncomeWithoutRSUs - personalAllowanceWithoutRSUs), 
      0
    );
    
    // Calculate tax on non-RSU income using the appropriate tax method
    const taxWithoutRSUs = isTaxResidentInScotland 
      ? calculateScottishTax(taxableIncomeWithoutRSUsLessPA, personalAllowanceWithoutRSUs, taxConstants).total
      : calculateUKTax(taxableIncomeWithoutRSUsLessPA, taxConstants).total;
    
    // RSU tax is the difference
    return Math.max(totalTax - taxWithoutRSUs, 0);
  };

  // Get the actual tax attributable to RSUs
  const actualRSUTax = calculateActualRSUTax();

  // Calculate the actual effective tax rate on RSUs
  const actualRSUTaxRate = RSUssubjecttoincometax > 0 
    ? (actualRSUTax / RSUssubjecttoincometax) * 100 
    : 0;

  // The adjustment needed (positive if more tax is due, negative if refund is due)
  const rsuTaxAdjustment = actualRSUTax - rsuTaxWithheld;

  // For reporting purposes, determine if this is additional tax due or a refund
  const rsuAdditionalTaxDue = rsuTaxAdjustment > 0 ? rsuTaxAdjustment : 0;
  const rsuRefundDue = rsuTaxAdjustment < 0 ? Math.abs(rsuTaxAdjustment) : 0;

  // Calculate the PAYE estimated tax (what would show on your payslip before special withholding)
  // This is an approximation of what would show in the standard PAYE calculation
  const estimatedPayeRsuTax = isTaxResidentInScotland
    ? RSUssubjecttoincometax * 0.42  // Higher rate in Scotland 
    : RSUssubjecttoincometax * 0.40;  // Higher rate in rest of UK

  const rsuTax = {
    total: actualRSUTax,
    withheld: rsuTaxWithheld,
    adjustment: rsuTaxAdjustment
  };

  const nonRsuTax = {
    total: taxResult.total - actualRSUTax
  };

  // The total tax is now calculated correctly based on all income sources
  const totalTax = taxResult.total;

  // Create tax breakdown object with empty tax breakdown for excluded RSUs
  const taxBreakdownExclRSUs = {
    totalTax: taxResultExclRSUs,
    rsuTax: isTaxResidentInScotland ? {
      total: 0,
      starterRate: 0,
      basicRate: 0,
      intermediateRate: 0,
      higherRate: 0,
      advancedRate: 0,
      topRate: 0
    } : {
      total: 0,
      basicRate: 0,
      higherRate: 0,
      additionalRate: 0
    },
    nonRsuTax: taxResultExclRSUs
  };

  // Calculate NIC
  const nicTotal = calculateNIC(taxableIncomeLessPension, numericRSUs, taxConstants);
  const nicTotalexclRSUs = calculateNIC(taxableIncomeLessPensionexclRSUs, 0, taxConstants);

  // Calculate total deductions
  const TotalDeductions = nicTotal + totalTax + capital_gains_tax + 
    (isTaxResidentInScotland ? 0 : numericVolDeductions) + studentLoanRepayments.total;
  const TotalDeductionsexclRSUs = nicTotalexclRSUs + taxResultExclRSUs.total + 
    (isTaxResidentInScotland ? 0 : numericVolDeductions) + studentLoanRepaymentsExclRSUs.total;

  // Calculate net salary
  const NetSalary = taxableIncomeLessPension + numericCG - TotalDeductions;
  const NetSalaryAndPension = NetSalary + totalPensionAmount;
  const NetSalaryexclRSU = taxableIncomeLessPensionexclRSUs - TotalDeductionsexclRSUs;
  const NetSalaryAndPensionexclRSU = NetSalaryexclRSU + totalPensionAmount;

  // Calculate monthly amounts
  const monthlyDivisor = 12;
  const MonthlyNetSalary = NetSalary / monthlyDivisor;
  const MonthlyNetSalaryexclRSU = NetSalaryexclRSU / monthlyDivisor;
  const MonthlyNetSalaryAndPension = NetSalaryAndPension / monthlyDivisor;
  const MonthlyNetSalaryAndPensionexclRSU = NetSalaryAndPensionexclRSU / monthlyDivisor;
  const MonthlyTotalTax = totalTax / monthlyDivisor;
  const MonthlyTotalTaxexclRSU = taxResultExclRSUs.total / monthlyDivisor;
  const MonthlyNIC = nicTotal / monthlyDivisor;
  const MonthlyNICexclRSU = nicTotalexclRSUs / monthlyDivisor;
  const MonthlyTotalPension = totalPensionAmount / monthlyDivisor;
  const MonthlyTaxableIncome = taxableIncomeLessPandPA / monthlyDivisor;
  const MonthlyTaxableIncomeexclRSU = taxableIncomeLessPandPAexclRSUs / monthlyDivisor;
  const MonthlyBaseSalary = numericBaseSalary / monthlyDivisor;
  const MonthlyBonus = numericBonus / monthlyDivisor;
  const MonthlyIncome = (numericBaseSalary + numericBonus) / monthlyDivisor;

  const rsuNICAmount = numericRSUs * (taxConstants.NIC_UPPER_RATE);
  const netRSUAmount = numericRSUs - rsuTax.withheld - rsuNICAmount;

  const rsuVestEventsWithPaye = rsuVestEvents && rsuVestEvents.length > 0 
    ? calculateVestPAYE(rsuVestEvents, isTaxResidentInScotland, applyEmployerNic) 
    : [];
  
  // Create the RSU tax explanation text
  const rsuTaxExplanation = applyEmployerNic 
    ? `In your case, employer NIC (${rsuemployerNICrate}%) is deducted from RSUs before income tax is calculated` 
    : `In your case, employer NIC is not deducted from RSUs before income tax is calculated`;

  // Add information about default behavior vs selected behavior
  const defaultBehaviorDiffers = defaultIsEmployerNicLiable !== applyEmployerNic;
  const defaultExplanation = defaultIsEmployerNicLiable 
    ? `By default in tax year ${taxYear}, employer NIC would be deducted from RSUs` 
    : `By default in tax year ${taxYear}, employer NIC would not be deducted from RSUs`;

  return {
    id: resultId,
    base_salary: numericBaseSalary,
    bonus: numericBonus,
    rsus: numericRSUs,
    capital_gains: safeToFixed(numericCG),
    cgt_rate: safeToFixed(cgt_rate * 100), // Percentage
    cgt_taxable_gains: safeToFixed(cgt_taxable_gains),
    capital_gains_tax: safeToFixed(capital_gains_tax),
    voluntary_deductions: numericVolDeductions,
    employee_pension_contribution_pct: employeePensionContributionPct,
    employer_pension_contribution_pct: employerPensionContributionPct,
    employee_pension_amount: safeToFixed(employeePensionAmount),
    employer_pension_amount: safeToFixed(employerPensionAmount),
    total_pension_amount: safeToFixed(totalPensionAmount),
    gross_pay: safeToFixed(gross_pay),
    gross_pay_ex_rsu: safeToFixed(gross_pay_ex_rsu),
    taxable_income_less_pension: safeToFixed(taxableIncomeLessPension),
    personal_allowance_reduction: safeToFixed(personalallowanceReduction),
    total_tax: safeToFixed(totalTax),
    rsu_tax: safeToFixed(actualRSUTax),
    rsu_actual_tax_rate: safeToFixed(actualRSUTaxRate),
    rsu_tax_withheld: safeToFixed(rsuTaxWithheld),
    rsu_tax_adjustment: safeToFixed(rsuTaxAdjustment),
    non_rsu_tax: safeToFixed(nonRsuTax.total),      
    total_nic: safeToFixed(nicTotal),
    net_salary: safeToFixed(NetSalary),
    net_salary_excl_rsu: safeToFixed(NetSalaryexclRSU),
    net_salary_inc_pension: safeToFixed(NetSalaryAndPension),
    net_salary_inc_pension_ex_RSU: safeToFixed(NetSalaryAndPensionexclRSU),
    monthly_net_salary: safeToFixed(MonthlyNetSalary),
    monthly_net_salary_inc_pension: safeToFixed(MonthlyNetSalaryAndPension),
    monthly_net_salary_ex_RSU: safeToFixed(MonthlyNetSalaryexclRSU),  
    monthly_net_salary_inc_pension_ex_RSU: safeToFixed(MonthlyNetSalaryAndPensionexclRSU),
    monthly_total_tax: safeToFixed(MonthlyTotalTax),
    monthly_total_nic: safeToFixed(MonthlyNIC),
    monthly_total_pension: safeToFixed(MonthlyTotalPension),
    monthly_taxable_income: safeToFixed(MonthlyTaxableIncome),
    monthly_base_salary: safeToFixed(MonthlyBaseSalary),  
    monthly_bonus: safeToFixed(MonthlyBonus),
    monthly_income: safeToFixed(MonthlyIncome),
    taxable_income_less_pension_excl_rsus: safeToFixed(taxableIncomeLessPensionexclRSUs),
    personal_allowance_reduction_excl_rsus: safeToFixed(personalallowanceReductionexclRSUs),
    total_tax_excl_rsus: safeToFixed(taxResultExclRSUs.total),
    total_nic_excl_rsus: safeToFixed(nicTotalexclRSUs), 
    monthly_tax_excl_rsu: safeToFixed(MonthlyTotalTaxexclRSU),
    monthly_nic_excl_rsu: safeToFixed(MonthlyNICexclRSU),
    monthly_taxable_income_excl_rsu: safeToFixed(MonthlyTaxableIncomeexclRSU),

    // RSU new
    rsu_additional_tax_due: safeToFixed(rsuAdditionalTaxDue),
    rsu_refund_due: safeToFixed(rsuRefundDue),
    rsu_tax_rate_applied: safeToFixed(averageRsuTaxRate),
    estimated_paye_rsu_tax: safeToFixed(estimatedPayeRsuTax),
    rsu_vest_events_with_paye: rsuVestEventsWithPaye,
    rsu_employer_nic_applied: applyEmployerNic,
    rsu_employer_nic_rate: rsuemployerNICrate,
    rsu_tax_year_explanation: { rsuExplanation: rsuTaxExplanation },

    // For non-Scottish residents - INCLUDED RSUs
    tax_basic_rate: isTaxResidentInScotland ? safeToFixed(taxResult.basicRate) : safeToFixed(taxResult.basicRate),
    tax_higher_rate: isTaxResidentInScotland ? safeToFixed(taxResult.higherRate) : safeToFixed(taxResult.higherRate),
    tax_additional_rate: isTaxResidentInScotland ? safeToFixed(taxResult.advancedRate) : safeToFixed(taxResult.additionalRate),
    rsu_tax_basic_rate: isTaxResidentInScotland ? safeToFixed(rsuTax.basicRate) : safeToFixed(rsuTax.basicRate),
    rsu_tax_higher_rate: isTaxResidentInScotland ? safeToFixed(rsuTax.higherRate) : safeToFixed(rsuTax.higherRate),
    rsu_tax_additional_rate: isTaxResidentInScotland ? safeToFixed(rsuTax.advancedRate) : safeToFixed(rsuTax.additionalRate),
    non_rsu_tax_basic_rate: isTaxResidentInScotland ? safeToFixed(nonRsuTax.basicRate) : safeToFixed(nonRsuTax.basicRate),
    non_rsu_tax_higher_rate: isTaxResidentInScotland ? safeToFixed(nonRsuTax.higherRate) : safeToFixed(nonRsuTax.higherRate),
    non_rsu_tax_additional_rate: isTaxResidentInScotland ? safeToFixed(nonRsuTax.advancedRate) : safeToFixed(nonRsuTax.additionalRate),
    
    // For Scottish residents - INCLUDED RSUs
    tax_starter_rate: isTaxResidentInScotland ? safeToFixed(taxResult.starterRate) : "0.00",
    tax_intermediate_rate: isTaxResidentInScotland ? safeToFixed(taxResult.intermediateRate) : "0.00",
    tax_top_rate: isTaxResidentInScotland ? safeToFixed(taxResult.topRate) : "0.00",
    rsu_tax_starter_rate: isTaxResidentInScotland ? safeToFixed(rsuTax.starterRate) : "0.00",
    rsu_tax_intermediate_rate: isTaxResidentInScotland ? safeToFixed(rsuTax.intermediateRate) : "0.00",
    rsu_tax_top_rate: isTaxResidentInScotland ? safeToFixed(rsuTax.topRate) : "0.00",
    non_rsu_tax_starter_rate: isTaxResidentInScotland ? safeToFixed(nonRsuTax.starterRate) : "0.00",
    non_rsu_tax_intermediate_rate: isTaxResidentInScotland ? safeToFixed(nonRsuTax.intermediateRate) : "0.00",
    non_rsu_tax_top_rate: isTaxResidentInScotland ? safeToFixed(nonRsuTax.topRate) : "0.00",
    
    // For EXCLUDED RSUs tax
    rsu_tax_rate_applied: safeToFixed(rsuTaxRate),
    net_rsu_amount: safeToFixed(netRSUAmount),
    rsu_nic_amount: safeToFixed(rsuNICAmount),
    rsu_tax_excl_rsus: "0.00", // Always zero when excluding RSUs
    non_rsu_tax_excl_rsus: safeToFixed(taxResultExclRSUs.total),

    // Add a field to indicate whether additional tax is due on RSUs:
    rsu_additional_tax_due: rsuTaxAdjustment > 0 ? safeToFixed(rsuTaxAdjustment) : "0.00",
    rsu_refund_due: rsuTaxAdjustment < 0 ? safeToFixed(Math.abs(rsuTaxAdjustment)) : "0.00",

    rsus_subject_to_income_tax: safeToFixed(RSUssubjecttoincometax),  
    captial_gains_tax: safeToFixed(capital_gains_tax),
    rsu_vest_events: rsuVestEvents.map(vest => ({
      ...vest,
      rsu_subject_to_tax: vest.amount - (vest.amount * (vest.employer_nic_rate || 6.9) / 100),
      net_after_withholding: vest.amount - (vest.amount * (vest.withholding_rate) / 100)
    })),
    rsu_tax_withheld_at_vest: safeToFixed(rsuTaxWithheld),
    rsu_tax_rate_applied: safeToFixed(effectiveRsuTaxRate),

    // new fields for student loans
    student_loan_repayments: safeToFixed(studentLoanRepayments.total),
    student_loan_repayments_excl_rsus: safeToFixed(studentLoanRepaymentsExclRSUs.total),
    student_loan_breakdown: studentLoanRepayments.breakdown,
    student_loan_breakdown_excl_rsus: studentLoanRepaymentsExclRSUs.breakdown,
    monthly_student_loan_repayments: safeToFixed(studentLoanRepayments.total / 12),
    monthly_student_loan_repayments_excl_rsus: safeToFixed(studentLoanRepaymentsExclRSUs.total / 12),
    has_student_loan_repayments: studentLoanRepayments.hasRepayments
  };
};

// Main calculation function
export const calculateSalaryDetails = (inputs, taxYear = "2024/2025") => {
  const pensionPct = inputs.employeePensionContributionPct !== undefined 
    ? inputs.employeePensionContributionPct 
    : (inputs.employeePensionContribution || 0);
  
  if (process.env.NODE_ENV !== 'production') {
    console.log(`salaryCalculations: Using pension % = ${pensionPct}`);
  }
  
  // Generate results array with standard 0.5% increments
  const results = Array.from({ length: 201 }, (_, index) => {
    const employeePensionContributionPct = index / 2; // This gives values 0, 0.5, 1.0, ... 100.0
    return calculateSalaryDetailsForPensionPct(inputs, taxYear, employeePensionContributionPct, index + 1);
  });

  // Check if we need to add a custom percentage calculation
  if (pensionPct > 0) {
    // Check if the custom percentage is already included in the results within a small margin
    const exists = results.some(r => 
      Math.abs(parseFloat(r.employee_pension_contribution_pct) - pensionPct) < 0.1
    );
    
    if (!exists) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`salaryCalculations: Adding custom pension % = ${pensionPct}`);
      }
      
      // Calculate a result with the exact custom percentage
      const customResult = calculateSalaryDetailsForPensionPct(
        inputs, 
        taxYear, 
        pensionPct, 
        `custom-${pensionPct}`
      );
      
      // Insert the custom result in the appropriate position
      let insertIndex = results.findIndex(r => 
        parseFloat(r.employee_pension_contribution_pct) > pensionPct
      );
      if (insertIndex === -1) insertIndex = results.length;
      
      results.splice(insertIndex, 0, customResult);
    }
  }

  return results;
};

// Utility function for formatting numbers
export const formatNumber = (num) => {
  if (!num) return "0.00";
  const parsed = parseFloat(num);
  return parsed.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Utility function for parsing input values
export const parseInputValue = (value) => {
  return value.replace(/,/g, '');
};